'use client'

import { useState } from 'react'
import Link from 'next/link'
import {
  Bot,
  Layers,
  FileText,
  Monitor,
  Zap,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  Mail,
  Loader2,
  Check,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import ReadinessRadar from './ReadinessRadar'
import ReadinessScoreCard from './ReadinessScoreCard'
import PlatformCTA from '@/components/platform/PlatformCTA'

interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: { text: string; status: 'good' | 'warning' | 'bad' }[]
}

interface AiReadinessResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: {
    botDiscoverability: CategoryResult
    structuredData: CategoryResult
    llmCommunication: CategoryResult
    contentAccessibility: CategoryResult
    crossSignalReadiness: CategoryResult
  }
  crawlCheckAvailable: boolean
  llmsValidatorAvailable: boolean
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface ReadinessResultsProps {
  result: AiReadinessResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share row, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score ring, crawl/llms availability badges, radar, AI
   * analysis, category cards, recommendations with blog links) is identical
   * either way.
   */
  variant?: 'public' | 'dashboard'
}

const CATEGORY_META: {
  key: keyof AiReadinessResult['categories']
  label: string
  icon: React.ReactNode
}[] = [
  {
    key: 'botDiscoverability',
    label: 'Bot Discoverability',
    icon: <Bot size={18} />,
  },
  {
    key: 'structuredData',
    label: 'Structured Data',
    icon: <Layers size={18} />,
  },
  {
    key: 'llmCommunication',
    label: 'LLM Communication',
    icon: <FileText size={18} />,
  },
  {
    key: 'contentAccessibility',
    label: 'Content Accessibility',
    icon: <Monitor size={18} />,
  },
  {
    key: 'crossSignalReadiness',
    label: 'Cross-Signal Readiness',
    icon: <Zap size={18} />,
  },
]

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const BLOG_LINKS: Record<string, string> = {
  'Bot Access': '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  'robots.txt': '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  'Structured Data': '/blogs/knowledge-graph-llm-visibility-real-data',
  'llms.txt': '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  Content: '/blogs/build-brand-ai-search-engines-cite',
  'Create llms.txt':
    '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  'Meta Robots': '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  Structure: '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  Links: '/blogs/knowledge-graph-llm-visibility-real-data',
  'Entity Definitions': '/blogs/build-brand-ai-search-engines-cite',
  'Use Policy': '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
  Completeness: '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  Access: '/blogs/llms-txt-specification-guide-what-it-is-how-it-works',
}

export default function ReadinessResults({
  result,
  variant = 'public',
}: ReadinessResultsProps) {
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/ai-readiness/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          url: result.url,
          domain: result.domain,
          overallScore: result.overallScore,
          grade: result.grade,
          categories: CATEGORY_META.map(m => ({
            label: m.label,
            score: result.categories[m.key].score,
            maxScore: result.categories[m.key].maxScore,
            color: result.categories[m.key].color,
          })),
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  const radarCategories = CATEGORY_META.map(m => ({
    label: m.label.replace('Cross-Signal Readiness', 'Cross-Signal'),
    score: result.categories[m.key].score,
    maxScore: result.categories[m.key].maxScore,
    color: result.categories[m.key].color,
  }))

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>
              AI Readiness Score for
            </p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.url}
            </p>
          </div>
          <OverallScore score={result.overallScore} grade={result.grade} />

          {/* Data availability badges */}
          <div className='flex flex-wrap justify-center gap-2 mt-2'>
            <span
              className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
              style={{
                backgroundColor: result.crawlCheckAvailable
                  ? '#22c55e15'
                  : '#F90B8A15',
                color: result.crawlCheckAvailable ? '#22c55e' : '#F90B8A',
              }}
            >
              {result.crawlCheckAvailable ? (
                <CircleCheck size={12} />
              ) : (
                <AlertTriangle size={12} />
              )}
              Crawl data
            </span>
            <span
              className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
              style={{
                backgroundColor: result.llmsValidatorAvailable
                  ? '#22c55e15'
                  : '#F90B8A15',
                color: result.llmsValidatorAvailable ? '#22c55e' : '#F90B8A',
              }}
            >
              {result.llmsValidatorAvailable ? (
                <CircleCheck size={12} />
              ) : (
                <AlertTriangle size={12} />
              )}
              llms.txt data
            </span>
          </div>
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only */}
      {variant !== 'dashboard' && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {/* Radar Chart */}
      <LenisFadeIn>
        <ReadinessRadar categories={radarCategories} />
      </LenisFadeIn>

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='AI Readiness'
          score={result.overallScore}
          grade={result.grade}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Category Score Cards */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {CATEGORY_META.map(m => {
            const cat = result.categories[m.key]
            return (
              <ReadinessScoreCard
                key={m.key}
                title={m.label}
                icon={m.icon}
                score={cat.score}
                maxScore={cat.maxScore}
                findings={cat.findings}
                color={cat.color}
              />
            )
          })}
        </div>
      </LenisStaggered>

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Action Roadmap
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #8c1b4c, #b32a6c, #e54b8c)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want to improve your AI readiness?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              We build knowledge graphs, llms.txt files, and structured data
              strategies that make AI search engines cite your brand. Backed by
              real implementation experience, not theory.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#b32a6c'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <Link
                href='/platform'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Or DIY with Radar (free){' '}
                <ArrowRight size={16} className='ml-1' />
              </Link>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
